/** Dev21 — RSVP. Validated reply form with a "Will You Join Us?" gate and animated success state. */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, ExternalLink } from 'lucide-react';
import { Section, SectionHeading } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content } from '../content';
import { useRsvp } from '@/src/modules/rsvp';

interface FormState {
  name: string;
  email: string;
  attending: 'yes' | 'no' | '';
  guests: string;
  message: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const EMPTY: FormState = {
  name: '',
  email: '',
  attending: '',
  guests: '1',
  message: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = 'Please enter your name.';
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim()))
    errors.email = 'Please enter a valid email address.';
  if (!form.attending) errors.attending = 'Let us know if you can make it.';
  if (form.attending === 'yes') {
    const n = Number(form.guests);
    if (!Number.isInteger(n) || n < 1 || n > 10)
      errors.guests = 'Guests must be a number between 1 and 10.';
  }
  return errors;
}

// ── Field shells ───────────────────────────────────────────────────────────

const fieldBase =
  'w-full rounded-xl border bg-[var(--d21-surface)] px-4 py-3 text-[var(--d21-ink)] outline-none transition focus:border-[var(--d21-accent)] focus:ring-2 focus:ring-[var(--d21-accent)]/20';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-left">
      <span className="text-sm font-medium text-[var(--d21-ink)]">{label}</span>
      {children}
      {error ? (
        <span className="text-xs font-medium text-red-600">{error}</span>
      ) : null}
    </label>
  );
}

export function RsvpSection({
  content,
  invitationId,
  isLive = false,
}: {
  content: Dev21Content['rsvp'];
  invitationId?: string | null;
  isLive?: boolean;
}) {
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [errors, setErrors] = React.useState<FormErrors>({});

  const { state, errorMessage, submit, reset } = useRsvp(invitationId, isLive);
  const submitted = state === 'success';
  const submitting = state === 'loading';

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    await submit({
      guestName: form.name.trim(),
      guestEmail: form.email.trim() || undefined,
      status: form.attending === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: form.attending === 'yes' ? (Number(form.guests) || 1) : 1,
      message: form.message.trim() || undefined,
    });
  };

  return (
    <Section id="rsvp" tone="ink">

      <SectionHeading
        label={content.label}
        title={content.title}
        intro={content.description}
        invert
      />

      <p className="mt-3 text-center text-sm text-[var(--d21-cream)]/55">
        Kindly respond by {content.deadline}
      </p>

      <div className="mx-auto mt-10 w-full max-w-xl">
        {/* External RSVP link takes precedence when supplied. */}
        {content.externalLink ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-md">
            <a
              href={content.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--d21-accent)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-[var(--d21-ink)]"
            >
              <ExternalLink size={16} />
              Open RSVP Form
            </a>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {submitted ? (
              /* ── Success card ── */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: DEV21_EASE }}
                className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur-md"
              >
                <motion.span
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: DEV21_EASE }}
                  className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[var(--d21-accent)] text-white"
                >
                  <Check size={36} />
                </motion.span>
                <h3 className="mt-6 font-serif text-2xl font-semibold text-[var(--d21-cream)]">
                  Thank you, {form.name.split(' ')[0]}!
                </h3>
                <p className="mt-2 text-[var(--d21-cream)]/65">
                  {form.attending === 'yes'
                    ? "We can't wait to celebrate with you."
                    : "We'll miss you — thank you for letting us know."}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setForm(EMPTY);
                    reset();
                    setShowForm(false);
                  }}
                  className="mt-6 text-sm font-medium text-[var(--d21-accent-soft)] underline underline-offset-4"
                >
                  Submit another response
                </button>
              </motion.div>
            ) : !showForm ? (
              /* ── Gate card: "Will You Join Us?" ── */
              <motion.div
                key="gate"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: DEV21_EASE }}
                className="rounded-3xl border border-white/10 bg-white/[0.06] p-10 text-center backdrop-blur-md"
              >
                <p className="text-5xl mb-4">✨</p>
                <h3 className="font-serif text-2xl font-semibold text-[var(--d21-cream)] mb-2">
                  Will You Join Us?
                </h3>
                <p className="text-[var(--d21-cream)]/55 text-sm mb-8">
                  Let us know if we can count on your presence.
                </p>
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  type="button"
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--d21-accent)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-[var(--d21-ink)]"
                >
                  RSVP Now
                </motion.button>
              </motion.div>
            ) : (
              /* ── RSVP form ── */
              <motion.form
                key="form"
                noValidate
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: DEV21_EASE }}
                className="flex flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.06] p-7 backdrop-blur-md sm:p-9"
              >
                <Field label="Full name" error={errors.name}>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Your name"
                    className={`${fieldBase} ${errors.name ? 'border-red-500' : 'border-[var(--d21-border)]'}`}
                  />
                </Field>

                <Field label="Email address (optional)" error={errors.email}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@example.com"
                    className={`${fieldBase} ${errors.email ? 'border-red-500' : 'border-[var(--d21-border)]'}`}
                  />
                </Field>

                <Field label="Will you be attending?" error={errors.attending}>
                  <div className="grid grid-cols-2 gap-3">
                    {(['yes', 'no'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => update('attending', option)}
                        className={`rounded-xl border px-4 py-3 text-sm font-medium capitalize transition ${
                          form.attending === option
                            ? 'border-[var(--d21-accent)] bg-[var(--d21-accent)] text-white'
                            : 'border-[var(--d21-border)] bg-[var(--d21-surface)] text-[var(--d21-ink)] hover:border-[var(--d21-accent)]'
                        }`}
                      >
                        {option === 'yes' ? 'Joyfully accepts' : 'Regretfully declines'}
                      </button>
                    ))}
                  </div>
                </Field>

                {form.attending === 'yes' ? (
                  <Field label="Number of guests" error={errors.guests}>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={form.guests}
                      onChange={(e) => update('guests', e.target.value)}
                      className={`${fieldBase} ${errors.guests ? 'border-red-500' : 'border-[var(--d21-border)]'}`}
                    />
                  </Field>
                ) : null}

                <Field label="A note for the couple (optional)">
                  <textarea
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    rows={3}
                    placeholder="Send your wishes…"
                    className={`${fieldBase} resize-none border-[var(--d21-border)]`}
                  />
                </Field>

                {state === 'error' && errorMessage && (
                  <p className="text-red-500 text-sm text-center">{errorMessage}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-1 rounded-full bg-[var(--d21-accent)] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-[var(--d21-ink)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : 'Send RSVP'}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        )}
      </div>
    </Section>
  );
}
